import type { ApiErrorBody, ErrorCode } from '@bifeai/shared';

const BASE = `${import.meta.env.VITE_API_URL ?? ''}/api/v1`;

/** Error de la API con su código, mensaje en español y errores por campo. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode,
    message: string,
    readonly requestId: string | null,
    readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Json = Record<string, unknown> | unknown[];

async function request<T>(method: string, path: string, body?: Json): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'INTERNAL_ERROR', 'No hay conexión con el servidor. Revisá internet y probá de nuevo.', null);
  }

  if (res.status === 204) return undefined as T;
  const data: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (data as ApiErrorBody | null)?.error;
    throw new ApiError(
      res.status,
      err?.code ?? 'INTERNAL_ERROR',
      err?.message ?? 'Ocurrió un error inesperado.',
      err?.requestId ?? res.headers.get('X-Request-Id'),
      err?.fields,
    );
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: Json) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: Json) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: Json) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

export function isApiError(e: unknown, ...codes: ErrorCode[]): e is ApiError {
  return e instanceof ApiError && (codes.length === 0 || codes.includes(e.code));
}

/** Pasa los errores por campo de la API a react-hook-form. */
export function applyFieldErrors(err: unknown, setError: (name: never, e: { message: string }) => void): boolean {
  if (!(err instanceof ApiError) || !err.fields) return false;
  for (const [name, message] of Object.entries(err.fields)) setError(name as never, { message });
  return true;
}

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Ocurrió un error inesperado.';
}
